import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Header from "../../components/ui/Header";
import Footer from "../../components/ui/Footer";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";

// Custom Audio Bindings
import { useDJAudioSystem } from "./hooks/useDJAudioSystem";

// Sub-components
import Deck from "./components/Deck";
import Mixer from "./components/Mixer";
import Playlist from "./components/Playlist";
import Sampler from "./components/Sampler";
import MicConsole from "./components/MicConsole";
import HardwareConfig from "./components/HardwareConfig";

const DJStudio = () => {
  const {
    isSupported,
    initialized,
    initEngine,
    deckStates,
    masterVolume,
    crossfader,
    micState,
    availableMics,
    availableOutputs,
    selectedOutputId,
    duckingFactor,
    recordingState,
    diagnostics,

    // Actions
    addDeck,
    removeDeck,
    loadTrack,
    loadTrackFromUrl,
    playDeck,
    pauseDeck,
    seekDeck,
    setDeckVolume,
    setDeckPan,
    setDeckPitch,
    nudgeDeckPitch,
    syncDeckBpm,
    setDeckLoop,
    setDeckCrossfadeSide,
    triggerCue,
    setCuePoint,
    clearCuePoint,
    setDeckEqLow,
    setDeckEqMid,
    setDeckEqHigh,
    setDeckFilter,
    setDeckStemVocal,
    setDeckStemInstrumental,
    setDeckStemBeats,
    setDeckStemMode,
    setDeckDelay,
    setDeckReverb,
    setDeckDistortion,
    setDeckMute,
    setDeckSolo,
    scratchDeck,
    releaseScratch,

    setCrossfader,
    setMasterVolume,
    setAudioOutputDevice,

    startMicrophone,
    stopMicrophone,
    selectMicDevice,
    setMicInputMode,
    setMicVolume,
    setMicEqLow,
    setMicEqMid,
    setMicEqHigh,
    setMicEchoDelay,
    setMicEchoRepeats,
    applyVoiceFx,
    setAutoDucking,
    setDuckingFactor,

    playSample,

    startRecording,
    stopRecording,
    clearRecordingResult,
  } = useDJAudioSystem();

  const [activeTab, setActiveTab] = useState("library"); // library | sampler | settings
  const [sessionTime, setSessionTime] = useState("00:00:00");

  // Format Elapsed Session Time
  useEffect(() => {
    const bootTime = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - bootTime;
      const hours = Math.floor(elapsed / 3600000)
        .toString()
        .padStart(2, "0");
      const minutes = Math.floor((elapsed % 3600000) / 60000)
        .toString()
        .padStart(2, "0");
      const seconds = Math.floor((elapsed % 60000) / 1000)
        .toString()
        .padStart(2, "0");
      setSessionTime(`${hours}:${minutes}:${seconds}`);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Keyboard Shortcuts (Space bar maps to active player play/pause)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if writing in inputs
      if (e.target.tagName === "INPUT" || e.target.tagName === "SELECT") return;

      if (e.code === "Space") {
        e.preventDefault();
        // Play/Pause Deck A by default
        if (deckStates.A) {
          if (deckStates.A.isPlaying) pauseDeck("A");
          else playDeck("A");
        }
      } else if (e.code === "KeyS" && e.shiftKey) {
        // Shift+S: Play/Pause Deck B
        if (deckStates.B) {
          if (deckStates.B.isPlaying) pauseDeck("B");
          else playDeck("B");
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [deckStates, playDeck, pauseDeck]);

  // Load handler helper
  const handleLoadTrackToDeck = async (deckId, fileOrUrl, metadata = {}) => {
    // Lazy activate Web Audio on first user interaction
    if (!initialized) {
      await initEngine();
    }

    if (metadata.isUrl) {
      await loadTrackFromUrl(deckId, fileOrUrl, metadata);
    } else {
      await loadTrack(deckId, fileOrUrl, metadata);
    }
  };

  const extraDecks = Object.keys(deckStates).filter(
    (id) => id !== "A" && id !== "B",
  );

  return (
    <>
      <Helmet>
        <title>Noob DJ Studio Console - DeveloperNoob</title>
        <meta
          name="description"
          content="Browser-based professional multi-channel DJ console with pitch shift, EQs, scratching, and vocal processing."
        />
        <meta
          name="keywords"
          content="DJ studio, serato, virtualdj, web audio API, scratching, crossfader, bpm detection, audio visualizer"
        />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background text-foreground relative overflow-x-hidden theme-transition font-inter">
        {/* Terminal Scanline overlay */}
        <div className="scan-lines fixed inset-0 pointer-events-none z-10"></div>

        {/* Navigation Header */}
        <Header />

        <main className="flex-1 pt-20 px-4 sm:px-6 lg:px-8 w-full max-w-[1600px] mx-auto flex flex-col space-y-4 pb-20">
          {/* Dashboard Header Bar */}
          <div className="bg-card border border-border rounded-lg p-3 flex flex-wrap items-center justify-between gap-4 terminal-glow relative overflow-hidden">
            {/* Logo & Status */}
            <div className="flex items-center space-x-3">
              <div className="p-1.5 bg-primary/10 border border-primary/20 rounded shadow-terminal-glow">
                <Icon
                  name="Sliders"
                  size={18}
                  className="text-primary animate-pulse"
                />
              </div>
              <div>
                <h1 className="text-sm font-fira font-bold uppercase tracking-wider text-foreground flex items-center space-x-2">
                  <span>DJ Studio Console</span>
                  <span className="text-[9px] bg-primary/10 border border-primary/30 text-primary px-1 rounded font-normal normal-case">
                    V1.0
                  </span>
                </h1>
                <p className="text-[10px] text-muted-foreground font-source">
                  Web Audio API Multi-Channel Mixing Console
                </p>
              </div>
            </div>

            {/* Session Stats */}
            <div className="flex items-center space-x-4 text-xs font-source text-muted-foreground">
              <div className="flex flex-col">
                <span className="text-[8px] uppercase text-zinc-500">
                  Session
                </span>
                <span className="text-foreground font-bold">{sessionTime}</span>
              </div>
              <div className="hidden sm:flex flex-col">
                <span className="text-[8px] uppercase text-zinc-500">
                  Audio Latency
                </span>
                <span className="text-foreground font-bold">
                  {diagnostics.latency} ms
                </span>
              </div>
              <div className="hidden sm:flex flex-col">
                <span className="text-[8px] uppercase text-zinc-500">
                  CPU LOAD
                </span>
                <div className="flex items-center space-x-1.5">
                  <div className="w-12 h-1.5 bg-zinc-950 rounded-full border border-zinc-800 overflow-hidden">
                    <div
                      className="h-full bg-success"
                      style={{ width: `${diagnostics.cpuUsage}%` }}
                    ></div>
                  </div>
                  <span className="text-foreground font-bold text-[9px]">
                    {diagnostics.cpuUsage}%
                  </span>
                </div>
              </div>
            </div>

            {/* Master Recorder Box */}
            <div className="flex items-center space-x-2">
              {recordingState.isRecording ? (
                <button
                  onClick={stopRecording}
                  className="px-3 py-1.5 bg-destructive text-white rounded font-bold text-xs uppercase tracking-wider flex items-center space-x-1.5 animate-pulse shadow-[0_0_15px_rgba(248,81,73,0.4)]"
                >
                  <Icon name="Square" size={10} fill="currentColor" />
                  <span>STOP REC ({recordingState.duration}s)</span>
                </button>
              ) : (
                <button
                  onClick={async () => {
                    await initEngine();
                    startRecording();
                  }}
                  className="px-3 py-1.5 border border-zinc-850 hover:border-red-500/40 text-red-500 rounded font-bold text-xs uppercase tracking-wider flex items-center space-x-1.5 hover:bg-red-500/5 transition"
                >
                  <Icon name="Circle" size={10} fill="currentColor" />
                  <span>RECORD SESSION</span>
                </button>
              )}

              {/* Download link for recorded tracks */}
              {recordingState.resultUrl && (
                <div className="flex items-center space-x-1">
                  <a
                    href={recordingState.resultUrl}
                    download={`DJ-Session-${Date.now()}.${recordingState.format || "mp3"}`}
                    className="px-3 py-1.5 bg-success text-white rounded font-bold text-xs uppercase tracking-wider flex items-center space-x-1.5 shadow-[0_0_10px_rgba(35,134,54,0.3)]"
                  >
                    <Icon name="Download" size={10} />
                    <span>DOWNLOAD {(recordingState.format || "mp3").toUpperCase()}</span>
                  </a>
                  <button
                    onClick={clearRecordingResult}
                    className="p-1.5 border border-zinc-850 text-muted-foreground hover:text-foreground rounded"
                    title="Clear recording"
                  >
                    <Icon name="Trash2" size={12} />
                  </button>
                </div>
              )}
            </div>

            {/* Audio Device & Dynamic Decks */}
            <div className="flex items-center space-x-2">
              {!deckStates.C && (
                <button
                  onClick={() => addDeck("C", "center")}
                  className="px-2.5 py-1.5 border border-zinc-850 hover:border-primary/40 text-[10px] font-fira font-bold uppercase rounded text-zinc-300 hover:text-primary transition"
                >
                  + DECK C
                </button>
              )}
              {!deckStates.D && (
                <button
                  onClick={() => addDeck("D", "center")}
                  className="px-2.5 py-1.5 border border-zinc-850 hover:border-primary/40 text-[10px] font-fira font-bold uppercase rounded text-zinc-300 hover:text-primary transition"
                >
                  + DECK D
                </button>
              )}

              <button
                onClick={initEngine}
                disabled={initialized}
                className={`px-3 py-1.5 rounded font-fira text-xs font-bold transition uppercase tracking-wider ${
                  initialized
                    ? "bg-zinc-950 border border-border text-muted-foreground cursor-default"
                    : "bg-primary text-black hover:shadow-terminal-glow cursor-pointer border border-primary"
                }`}
              >
                {initialized ? "Audio Connected" : "Connect Audio Context"}
              </button>
            </div>
          </div>

          {/* Browser compatibility check */}
          {!isSupported && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center font-fira">
              <Icon
                name="AlertTriangle"
                size={48}
                className="text-destructive mx-auto mb-4"
              />
              <h3 className="text-lg font-bold text-destructive mb-2 uppercase">
                AudioContext Not Supported
              </h3>
              <p className="text-xs text-muted-foreground max-w-xl mx-auto font-source">
                Your browser doesn't support Web Audio API natively. Please
                launch in modern environments like Chrome, Safari, Firefox, or
                Edge.
              </p>
            </div>
          )}

          {/* Console Layout: Decks & Mixer */}
          {isSupported && (
            <div className="flex flex-col space-y-4">
              {/* Primary Mix Grid */}
              <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-stretch w-full">
                {/* Column Left: DECK A */}
                <div className="xl:col-span-4 h-full">
                  <Deck
                    id="A"
                    deckState={deckStates.A}
                    playDeck={playDeck}
                    pauseDeck={pauseDeck}
                    seekDeck={seekDeck}
                    setDeckPitch={setDeckPitch}
                    nudgeDeckPitch={nudgeDeckPitch}
                    syncDeckBpm={syncDeckBpm}
                    setDeckLoop={setDeckLoop}
                    setDeckCrossfadeSide={setDeckCrossfadeSide}
                    triggerCue={triggerCue}
                    setCuePoint={setCuePoint}
                    clearCuePoint={clearCuePoint}
                    setDeckDelay={setDeckDelay}
                    setDeckReverb={setDeckReverb}
                    setDeckDistortion={setDeckDistortion}
                    setDeckMute={setDeckMute}
                    setDeckSolo={setDeckSolo}
                    scratchDeck={scratchDeck}
                    releaseScratch={releaseScratch}
                    otherDeckBpm={deckStates.B?.bpm}
                    color="primary"
                  />
                </div>

                {/* Column Center: Mixer */}
                <div className="xl:col-span-4 h-full">
                  <Mixer
                    deckStates={deckStates}
                    crossfader={crossfader}
                    masterVolume={masterVolume}
                    setCrossfader={setCrossfader}
                    setMasterVolume={setMasterVolume}
                    setDeckVolume={setDeckVolume}
                    setDeckEqLow={setDeckEqLow}
                    setDeckEqMid={setDeckEqMid}
                    setDeckEqHigh={setDeckEqHigh}
                    setDeckFilter={setDeckFilter}
                    setDeckStemVocal={setDeckStemVocal}
                    setDeckStemInstrumental={setDeckStemInstrumental}
                    setDeckStemBeats={setDeckStemBeats}
                    setDeckStemMode={setDeckStemMode}
                    setDeckPan={setDeckPan}
                  />
                </div>

                {/* Column Right: DECK B */}
                <div className="xl:col-span-4 h-full">
                  <Deck
                    id="B"
                    deckState={deckStates.B}
                    playDeck={playDeck}
                    pauseDeck={pauseDeck}
                    seekDeck={seekDeck}
                    setDeckPitch={setDeckPitch}
                    nudgeDeckPitch={nudgeDeckPitch}
                    syncDeckBpm={syncDeckBpm}
                    setDeckLoop={setDeckLoop}
                    setDeckCrossfadeSide={setDeckCrossfadeSide}
                    triggerCue={triggerCue}
                    setCuePoint={setCuePoint}
                    clearCuePoint={clearCuePoint}
                    setDeckDelay={setDeckDelay}
                    setDeckReverb={setDeckReverb}
                    setDeckDistortion={setDeckDistortion}
                    setDeckMute={setDeckMute}
                    setDeckSolo={setDeckSolo}
                    scratchDeck={scratchDeck}
                    releaseScratch={releaseScratch}
                    otherDeckBpm={deckStates.A?.bpm}
                    color="cyan"
                  />
                </div>
              </div>

              {/* Row: Dynamic Extra Decks (C & D) */}
              {extraDecks.length > 0 && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-2">
                  {deckStates.C && (
                    <div className="relative group">
                      <button
                        onClick={() => removeDeck("C")}
                        className="absolute top-2 right-2 z-10 px-2 py-0.5 bg-red-600/90 text-white rounded text-[8px] font-bold opacity-0 group-hover:opacity-100 transition shadow"
                      >
                        CLOSE DECK C
                      </button>
                      <Deck
                        id="C"
                        deckState={deckStates.C}
                        playDeck={playDeck}
                        pauseDeck={pauseDeck}
                        seekDeck={seekDeck}
                        setDeckPitch={setDeckPitch}
                        nudgeDeckPitch={nudgeDeckPitch}
                        syncDeckBpm={syncDeckBpm}
                        setDeckLoop={setDeckLoop}
                        setDeckCrossfadeSide={setDeckCrossfadeSide}
                        triggerCue={triggerCue}
                        setCuePoint={setCuePoint}
                        clearCuePoint={clearCuePoint}
                        setDeckDelay={setDeckDelay}
                        setDeckReverb={setDeckReverb}
                        setDeckDistortion={setDeckDistortion}
                        setDeckMute={setDeckMute}
                        setDeckSolo={setDeckSolo}
                        scratchDeck={scratchDeck}
                        releaseScratch={releaseScratch}
                        otherDeckBpm={deckStates.A?.bpm}
                        color="blue"
                      />
                    </div>
                  )}
                  {deckStates.D && (
                    <div className="relative group">
                      <button
                        onClick={() => removeDeck("D")}
                        className="absolute top-2 right-2 z-10 px-2 py-0.5 bg-red-600/90 text-white rounded text-[8px] font-bold opacity-0 group-hover:opacity-100 transition shadow"
                      >
                        CLOSE DECK D
                      </button>
                      <Deck
                        id="D"
                        deckState={deckStates.D}
                        playDeck={playDeck}
                        pauseDeck={pauseDeck}
                        seekDeck={seekDeck}
                        setDeckPitch={setDeckPitch}
                        nudgeDeckPitch={nudgeDeckPitch}
                        syncDeckBpm={syncDeckBpm}
                        setDeckLoop={setDeckLoop}
                        setDeckCrossfadeSide={setDeckCrossfadeSide}
                        triggerCue={triggerCue}
                        setCuePoint={setCuePoint}
                        clearCuePoint={clearCuePoint}
                        setDeckDelay={setDeckDelay}
                        setDeckReverb={setDeckReverb}
                        setDeckDistortion={setDeckDistortion}
                        setDeckMute={setDeckMute}
                        setDeckSolo={setDeckSolo}
                        scratchDeck={scratchDeck}
                        releaseScratch={releaseScratch}
                        otherDeckBpm={deckStates.A?.bpm}
                        color="warning"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Interactive Tabs: Playlist (Library) or Sampler FX on Mobile/Tablet */}
              <div className="mt-6 border-t border-border/40 pt-4 flex flex-col space-y-4">
                <div className="flex border-b border-border font-source text-xs pb-0.5 space-x-3 overflow-x-auto no-scrollbar whitespace-nowrap select-none">
                  <button
                    onClick={() => setActiveTab("library")}
                    className={`pb-1 border-b-2 font-bold px-2 tracking-wider flex-shrink-0 ${
                      activeTab === "library"
                        ? "border-primary text-primary"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    🎵 SONG LIBRARY & LOADER
                  </button>
                  <button
                    onClick={() => setActiveTab("sampler")}
                    className={`pb-1 border-b-2 font-bold px-2 tracking-wider flex-shrink-0 ${
                      activeTab === "sampler"
                        ? "border-primary text-primary"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    💥 FX SOUNDBOARD SAMPLER
                  </button>
                  <button
                    onClick={() => setActiveTab("mic")}
                    className={`pb-1 border-b-2 font-bold px-2 tracking-wider flex-shrink-0 ${
                      activeTab === "mic"
                        ? "border-primary text-primary"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    🎤 VOCAL MIC CONSOLE
                  </button>
                  <button
                    onClick={() => setActiveTab("hardware")}
                    className={`pb-1 border-b-2 font-bold px-2 tracking-wider flex-shrink-0 ${
                      activeTab === "hardware"
                        ? "border-primary text-primary"
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    ⚙️ HARDWARE CONFIG
                  </button>
                </div>

                <div className="animate-fadeIn mt-4 min-h-[440px] h-auto lg:h-[440px] flex flex-col justify-stretch">
                  {activeTab === "library" && (
                    <Playlist
                      onLoadToDeck={handleLoadTrackToDeck}
                      deckStates={deckStates}
                    />
                  )}
                  {activeTab === "sampler" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch h-full flex-grow animate-fadeIn">
                      <Sampler onTriggerSample={playSample} />

                      {/* Synthesizer instructions panel */}
                      <div className="bg-card border border-border rounded-lg p-4 font-source text-xs space-y-3 flex flex-col justify-between terminal-glow h-full">
                        <div className="space-y-3">
                          <h4 className="font-bold text-terminal-cyan uppercase flex items-center space-x-1.5 border-b border-border pb-1.5 flex-shrink-0">
                            <Icon name="Sliders" size={12} />
                            <span>Soundboard Synthesizer Info</span>
                          </h4>
                          <p className="text-muted-foreground leading-relaxed text-[11px]">
                            Rather than loading external static sound files, this
                            console synthesizes audio in real-time.
                          </p>
                          <ul className="list-disc pl-4 space-y-1.5 text-muted-foreground text-[10px]">
                            <li>
                              <strong className="text-foreground">Airhorn & Laser:</strong> Detuned sawtooth brass bursts and exponential frequency sweep drops.
                            </li>
                            <li>
                              <strong className="text-foreground">Sub Drop & Impact Boom:</strong> Heavy 808 sub-bass sine drop curves and low-frequency explosion booms.
                            </li>
                            <li>
                              <strong className="text-foreground">Siren & Riser Uplifter:</strong> Dual LFO emergency sirens and build-up bandpass filter pitch uplifters.
                            </li>
                            <li>
                              <strong className="text-foreground">Tape Stop & Vinyl Chirp:</strong> Turntable brake power-off pitch dives and FM backspin scratch chirps.
                            </li>
                            <li>
                              <strong className="text-foreground">Synth Drum Kit:</strong> Punchy 808 sub kick, noise snare with body, and high-pass hi-hat hiss.
                            </li>
                            <li>
                              <strong className="text-foreground">Claps & Cowbell:</strong> Multi-burst stereo reverb claps and classic dual-oscillator TR-808 cowbells.
                            </li>
                            <li>
                              <strong className="text-foreground">Beat Fills & Rolls:</strong> Rhythmic 4-step drum roll fills, trap rimshots, and triplet sub-kick rolls.
                            </li>
                          </ul>
                        </div>

                        <div className="text-[10px] text-terminal-cyan/80 bg-terminal-cyan/5 border border-terminal-cyan/10 p-2 rounded leading-tight flex-shrink-0">
                          <strong>Performance Tip:</strong> Synthesized soundboard triggers are lightweight and run entirely client-side on the Web Audio API thread with 0ms asset-load latency.
                        </div>
                      </div>
                    </div>
                  )}
                  {activeTab === "mic" && (
                    <div className="h-full flex-grow animate-fadeIn">
                      <MicConsole
                        micState={micState}
                        startMicrophone={startMicrophone}
                        stopMicrophone={stopMicrophone}
                        selectMicDevice={selectMicDevice}
                        setMicInputMode={setMicInputMode}
                        setMicVolume={setMicVolume}
                        setMicEqLow={setMicEqLow}
                        setMicEqMid={setMicEqMid}
                        setMicEqHigh={setMicEqHigh}
                        setMicEchoDelay={setMicEchoDelay}
                        setMicEchoRepeats={setMicEchoRepeats}
                        applyVoiceFx={applyVoiceFx}
                        setAutoDucking={setAutoDucking}
                        duckingFactor={duckingFactor}
                        setDuckingFactor={setDuckingFactor}
                        availableMics={availableMics}
                        color="primary"
                      />
                    </div>
                  )}
                  {activeTab === "hardware" && (
                    <div className="h-full flex-grow animate-fadeIn">
                      <HardwareConfig
                        availableMics={availableMics}
                        availableOutputs={availableOutputs}
                        selectedOutputId={selectedOutputId}
                        setAudioOutputDevice={setAudioOutputDevice}
                        selectMicDevice={selectMicDevice}
                        micState={micState}
                        startMicrophone={startMicrophone}
                        recordingState={recordingState}
                        startRecording={startRecording}
                        stopRecording={stopRecording}
                        clearRecordingResult={clearRecordingResult}
                        diagnostics={diagnostics}
                        color="primary"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
};

export default DJStudio;
